import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    User, Phone, Mail, MapPin, Briefcase, Calendar,
    BadgeCheck, AlertCircle, Hash, Heart, Flag, Landmark,
    Smile, Frown, Meh, Edit, Shield, Award, Users, UserPlus
} from 'lucide-react';

export default function SocioDatos({ socio, formatDate }) {
    const navigate = useNavigate();

    if (!socio) return null;

    return (
        <div className="space-y-6 animate-premium-in">
            {/* COMPACT PROFILE HEADER */}
            <div className="enterprise-card overflow-hidden">
                <div className="bg-slate-900 px-6 py-6 md:px-8 text-white">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6">
                            <div className="bg-white/10 p-4 rounded-lg flex-shrink-0 border border-white/5">
                                {socio.personaJuridica ? <Landmark className="w-8 h-8 text-coop-400" /> : <User className="w-8 h-8 text-coop-400" />}
                            </div>
                            <div className="text-center md:text-left">
                                <h1 className="text-xl font-extrabold tracking-tight mb-1 uppercase">
                                    {socio.personaJuridica
                                        ? socio.personaJuridica.razonSocial
                                        : `${socio.personaFisica?.nombres || ''} ${socio.personaFisica?.apellidos || ''}`
                                    }
                                </h1>
                                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                                    <span className="inline-flex items-center bg-white/5 border border-white/10 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        NRO. SOCIO: {socio.numeroSocio || 'PENDIENTE'}
                                    </span>
                                    <span className="inline-flex items-center bg-white/5 border border-white/10 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        ID SISTEMA: #{socio.id}
                                    </span>
                                    <span className="inline-flex items-center bg-white/5 border border-white/10 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-slate-400">
                                        {socio.personaJuridica ? `RUC: ${socio.personaJuridica.ruc}` : `${socio.personaFisica?.tipoDocumento?.nombreTipo || 'CI'}: ${socio.personaFisica?.numeroDocumento}`}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-2 shrink-0">
                             <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm border ${socio.estado?.id === 1 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20' :
                                socio.estado?.id === 5 ? 'bg-rose-500/20 text-rose-400 border-rose-500/20' :
                                    'bg-amber-500/20 text-amber-400 border-amber-500/20'
                                }`}>
                                {socio.estado?.descripcion || 'CONTACTAR'}
                            </span>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Vinculado: {formatDate(socio.fechaIngresoCooperativa)}</p>
                        </div>
                    </div>
                </div>

                {/* Sub-header status bar */}
                <div className="bg-slate-50 border-t border-slate-200 px-8 py-3 flex flex-wrap gap-6 items-center">
                     {socio.calificacionRiesgo && (
                        <div className="flex items-center gap-2">
                            <Shield className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-[11px] font-black text-slate-500 uppercase tracking-tight">Riesgo {socio.calificacionRiesgo.codigo}</span>
                        </div>
                    )}
                    {socio.nivelFidelidad && (
                        <div className="flex items-center gap-2">
                            <Award className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tighter">{socio.nivelFidelidad.descripcion}</span>
                        </div>
                    )}
                    <button
                        onClick={() => navigate(`/socios/editar/${socio.id}`)}
                        className="ml-auto flex items-center gap-2 text-coop-600 hover:text-coop-700 text-[11px] font-black uppercase tracking-widest transition-colors"
                    >
                        <Edit className="w-3.5 h-3.5" />
                        Ver/Editar Datos
                    </button>
                </div>
            </div>

            {/* Detailed Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Information Sections */}
                <div className="enterprise-card p-6 space-y-6">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-2 flex items-center gap-2">
                        <User className="w-3.5 h-3.5" />
                        Ficha {socio.personaJuridica ? 'Constitutiva' : 'Identitaria'}
                    </h3>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nacimiento / Registro</p>
                            <p className="text-[13px] font-bold text-slate-900 leading-tight">
                                {formatDate(socio.personaJuridica ? socio.personaJuridica.fechaConstitucion : socio.personaFisica?.fechaNacimiento)}
                                {socio.personaFisica?.lugarNacimiento && (
                                    <span className="block text-[10px] font-bold text-slate-400 mt-1 uppercase">
                                        Lugar: {socio.personaFisica.lugarNacimiento}
                                    </span>
                                )}
                            </p>
                        </div>
                        
                        {socio.personaJuridica ? (
                            <div className="grid grid-cols-1 gap-1">
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Representación Legal</p>
                                <p className="text-[13px] font-bold text-slate-900">{socio.personaJuridica.representanteLegal || '-'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 gap-1">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Género Biológico</p>
                                    <p className="text-[13px] font-bold text-slate-900">{socio.personaFisica?.genero?.descripcion || '-'}</p>
                                </div>
                                <div className="grid grid-cols-1 gap-1">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Estado Civil</p>
                                    <p className="text-[13px] font-bold text-slate-900">{socio.personaFisica?.estadoCivil?.descripcion || '-'}</p>
                                </div>
                                
                                {/* SOCIO PROPONENTE */}
                                <div className="grid grid-cols-1 gap-1 border-t border-slate-50 pt-2">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                        <UserPlus className="w-3 h-3" /> Socio Proponente
                                    </p>
                                    <p className="text-[11px] font-bold text-slate-900 uppercase">
                                        {socio.proponente ? socio.proponente.nombreCompleto : 'Ingreso Directo'}
                                        {socio.proponente?.numeroSocio && <span className="text-slate-400 ml-1"> (Nro. {socio.proponente.numeroSocio})</span>}
                                    </p>
                                </div>

                                {/* TUTORES (Si es Junior) */}
                                {socio.tipoSocio?.descripcion?.toUpperCase().includes('JUVENIL') && (
                                    <div className="grid grid-cols-1 gap-2 border-t border-slate-50 pt-2">
                                        <p className="text-[9px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-1">
                                            <Users className="w-3 h-3" /> Tutores Legales
                                        </p>
                                        <div className="space-y-2">
                                            {socio.tutores && socio.tutores.length > 0 ? (
                                                socio.tutores.map(t => (
                                                    <div key={t.id} className="bg-blue-50/50 p-2 rounded border border-blue-100/50">
                                                        <p className="text-[10px] font-black text-slate-800 uppercase">{t.tutor?.nombreCompleto}</p>
                                                        <p className="text-[9px] font-bold text-blue-600 uppercase tracking-tight">
                                                            {t.parentesco?.nombre || 'Tutor'} {t.esPrincipal && <span className="ml-1 text-[8px] bg-blue-600 text-white px-1 rounded">Principal</span>}
                                                        </p>
                                                        <p className="text-[8px] text-slate-400 mt-0.5">CI: {t.tutor?.numeroDocumento || t.tutor?.personaFisica?.cedula}</p>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-[10px] text-rose-400 font-bold italic">No se registran tutores</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="enterprise-card p-6 space-y-6">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-2 flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5" />
                        Domicilio y Contacto
                    </h3>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Teléfono Primario</p>
                            <p className="text-[13px] font-bold text-slate-900">
                                {socio.contactos?.find(c => c.tipoContacto === 'CELULAR' || c.tipoContacto === 'TELEFONO_FIJO')?.valor || '-'}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Correo Electrónico</p>
                            <p className="text-[13px] font-bold text-slate-900 break-all">
                                {socio.contactos?.find(c => c.tipoContacto === 'EMAIL')?.valor || '-'}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-1 border-t border-slate-50 pt-2">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Dirección Principal</p>
                            {(() => {
                                const dir = socio.direcciones?.find(d => d.esPrincipal) || socio.direcciones?.[0];
                                if (!dir) return <p className="text-[11px] font-bold text-slate-300 italic">No registra localización</p>;
                                return (
                                    <div className="text-[12px] font-bold text-slate-900 leading-tight">
                                        {dir.ciudadNombre}, {dir.departamentoNombre}
                                        <div className="text-[10px] font-medium text-slate-500 mt-1 uppercase leading-none">
                                            {dir.direccionExacta}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>

                <div className="enterprise-card p-6 space-y-6">
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-2 flex items-center gap-2">
                        <Briefcase className="w-3.5 h-3.5" />
                        Perfil Profesional
                    </h3>
                    
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Actividad Económica</p>
                            <p className="text-[13px] font-bold text-slate-900 leading-tight">
                                {socio.datosLaborales?.profesion?.nombre
                                    || socio.personaFisica?.profesionOcupacion
                                    || '-'}
                                {socio.datosLaborales?.empresa && (
                                    <span className="block text-[10px] font-bold text-emerald-600 mt-1 uppercase">
                                        @{socio.datosLaborales.empresa}
                                    </span>
                                )}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cédula de Identidad</p>
                            <p className="text-[13px] font-bold text-slate-900">
                                {socio.personaFisica?.numeroDocumento || socio.personaJuridica?.ruc || '-'}
                            </p>
                        </div>
                        <div className="grid grid-cols-1 gap-1">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nacionalidad Origen</p>
                            <p className="text-[13px] font-bold text-slate-900">{socio.nacionalidad?.pais || '-'}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
